import { useMutation } from '@apollo/client/react';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Space } from 'antd';
import clsx from 'clsx';
import React, { useContext, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import {
  BREAKPOINTS,
  GA_EVENT,
  GA_LABEL,
} from '../../../../../../common/constants';
import { Event } from '../../../../../../common/trackEvents';
import useRouter from '../../../../../../common/useRouter';
import { formValidatorRules } from '../../../../../../common/utils';
import {
  CREATE_INSTRUCTION_ANALYSIS,
  UPDATE_INSTRUCTION_ANALYSIS,
} from '../../../../graphql/Mutation';

const { required } = formValidatorRules;
const { TextArea } = Input;

const createCostValidation = (maxValue = 999999999) => [
  {
    validator: (_, value) => {
      if (value && value > maxValue) {
        return Promise.reject(
          new Error(`Value cannot exceed ${maxValue.toLocaleString()}`),
        );
      }
      return Promise.resolve();
    },
  },
];

const InstructionAnalysisModal = (props) => {
  const {
    showModal,
    setShowModal,
    instructionData,
    refetchInstructionData,
    isEditMode = false,
  } = props;
  const analysis = instructionData?.analysis;
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const {
    params: { projectId },
  } = useRouter();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [form] = Form.useForm();
  const [isManualTotal, setIsManualTotal] = useState(false);
  const [isErrorExist, setIsErrorExist] = useState(false);
  const [isUsingComponents, setIsUsingComponents] = useState(false);
  const [costComponents, setCostComponents] = useState({
    labour: 0,
    materials: 0,
    machinery: 0,
    others: 0,
    total: 0,
  });

  useMemo(() => {
    if (isEditMode && instructionData?.analysis) {
      const analysisData = instructionData?.analysis;
      setCostComponents({
        labour: analysisData?.labourCost || 0,
        materials: analysisData?.materialCost || 0,
        machinery: analysisData?.machineryCost || 0,
        others: analysisData?.otherCost || 0,
        total: analysisData?.totalCost || 0,
      });

      const hasComponentValues =
        analysisData?.labourCost > 0 ||
        analysisData?.materialCost > 0 ||
        analysisData?.machineryCost > 0 ||
        analysisData?.otherCost > 0;

      if (hasComponentValues) {
        setIsUsingComponents(true);
        setIsManualTotal(false);
      } else if (analysisData?.totalCost > 0) {
        setIsManualTotal(true);
        setIsUsingComponents(false);
      }
    }
  }, [isEditMode, instructionData?.analysis]);

  const [mutateInstructionAnalysis, { loading: submitLoading }] = useMutation(
    isEditMode ? UPDATE_INSTRUCTION_ANALYSIS : CREATE_INSTRUCTION_ANALYSIS,
    {
      onCompleted: () => {
        form.resetFields();
        setShowModal(false);
        if (refetchInstructionData) {
          refetchInstructionData();
        }
        Event(
          isEditMode
            ? GA_EVENT.UPDATE_INSTRUCTION_ANALYSIS
            : GA_EVENT.CREATE_INSTRUCTION_ANALYSIS,
          {
            label: isEditMode
              ? GA_LABEL.UPDATE_INSTRUCTION_ANALYSIS
              : GA_LABEL.CREATE_INSTRUCTION_ANALYSIS,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            instruction_id: instructionData?.id,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          },
        );
      },
      onError: () => {
        // Error handling is managed by Apollo error link
      },
    },
  );

  const handleCostComponentChange = (field, value) => {
    setIsErrorExist(false);
    const newCostComponents = {
      ...costComponents,
      [field]: value || 0,
    };
    setCostComponents(newCostComponents);
    const hasAnyComponent =
      newCostComponents?.labour > 0 ||
      newCostComponents?.materials > 0 ||
      newCostComponents?.machinery > 0 ||
      newCostComponents?.others > 0;

    if (hasAnyComponent) {
      setIsUsingComponents(true);
      setIsManualTotal(false);

      // Auto-calculate total
      const newTotal =
        (newCostComponents?.labour || 0) +
        (newCostComponents?.materials || 0) +
        (newCostComponents?.machinery || 0) +
        (newCostComponents?.others || 0);

      form.setFieldsValue({
        [`${field}Cost`]: value,
        totalCost: newTotal,
      });

      setCostComponents((prev) => ({
        ...prev,
        total: newTotal,
      }));
    } else {
      // No components have values, reset to neutral state
      setIsUsingComponents(false);
      setIsManualTotal(false);

      form.setFieldsValue({
        [`${field}Cost`]: value,
        totalCost: '',
      });

      setCostComponents((prev) => ({
        ...prev,
        total: 0,
      }));
    }
  };

  const handleTotalChange = (value) => {
    setIsErrorExist(false);
    if (value && value > 0) {
      setIsManualTotal(true);
      setIsUsingComponents(false);

      // Reset component fields
      setCostComponents({
        labour: 0,
        materials: 0,
        machinery: 0,
        others: 0,
        total: value || 0,
      });
    } else if (!value || value === 0) {
      // Reset to neutral state when total is cleared
      setIsManualTotal(false);
      setIsUsingComponents(false);

      setCostComponents((prev) => ({
        ...prev,
        total: 0,
      }));
    }
  };

  const handleInputKeyDown = (e) => {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'];
    if (
      !allowedKeys.includes(e.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowModal(false);
    setCostComponents({
      labour: 0,
      materials: 0,
      machinery: 0,
      others: 0,
      total: 0,
    });
    setIsManualTotal(false);
    setIsUsingComponents(false);
  };

  const onFinish = async (formValues) => {
    if (
      !(
        costComponents?.labour +
          costComponents?.materials +
          costComponents?.machinery +
          costComponents?.others +
          costComponents?.total >
        0
      )
    ) {
      setIsErrorExist(true);
      return;
    }
    try {
      const analysisData = {
        rootCauseAnalysis: formValues?.rootCauseAnalysis,
        correction: formValues?.correction || null,
        correctiveAction: formValues?.correctiveAction || null,
        labourCost: costComponents?.labour,
        materialCost: costComponents?.materials,
        machineryCost: costComponents?.machinery,
        otherCost: costComponents?.others,
        totalCost: costComponents?.total,
      };

      await mutateInstructionAnalysis({
        variables: {
          instructionId: instructionData?.id,
          data: analysisData,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating instruction analysis:', error);
    }
  };

  return (
    <Modal
      maskClosable={false}
      centered
      open={showModal}
      closable={false}
      footer={null}
      width={isDesktopViewport ? 720 : '90%'}
      className="instruction-analysis-modal"
    >
      <h2 className="mb-15">
        {isEditMode ? 'Edit' : 'Add'} Instruction Analysis
      </h2>

      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={
          isEditMode && analysis
            ? {
                ...analysis,
                labourCost: analysis?.labourCost > 0 && analysis?.labourCost,
                materialCost:
                  analysis?.materialCost > 0 && analysis?.materialCost,
                machineryCost:
                  analysis?.machineryCost > 0 && analysis?.machineryCost,
                otherCost: analysis?.otherCost > 0 && analysis?.otherCost,
                totalCost: analysis?.totalCost > 0 && analysis?.totalCost,
              }
            : undefined
        }
      >
        <Form.Item
          name="rootCauseAnalysis"
          label="Root Cause Analysis"
          rules={[
            required,
            {
              max: 1000,
              message: 'Maximum 1000 characters allowed',
            },
          ]}
        >
          <TextArea
            id="instructions-analysis-root-cause-textarea"
            rows={3}
            placeholder="Describe the root cause analysis..."
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          name="correction"
          label="Correction"
          rules={[
            required,
            {
              max: 1000,
              message: 'Maximum 1000 characters allowed',
            },
          ]}
        >
          <TextArea
            id="instructions-analysis-correction-textarea"
            rows={3}
            placeholder="Enter correction details..."
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          name="correctiveAction"
          label="Corrective Action"
          rules={[
            required,
            {
              max: 1000,
              message: 'Maximum 1000 characters allowed',
            },
          ]}
        >
          <TextArea
            id="instructions-analysis-corrective-action-textarea"
            rows={3}
            placeholder="Enter corrective action..."
            showCount
            maxLength={1000}
          />
        </Form.Item>
        <div
          className={clsx(
            'cost-of-quality-section',
            isErrorExist && 'error-message',
          )}
        >
          <h4 className="mb-15 required-label">Cost of Quality Components</h4>
          <Row gutter={[12, 0]}>
            <Col span={isDesktopViewport ? 12 : 24}>
              <Form.Item
                name="labourCost"
                label="Labour"
                rules={createCostValidation()}
              >
                <InputNumber
                  id="instructions-analysis-labour-cost-input"
                  className="width-percent-100"
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  disabled={isManualTotal}
                  onKeyDown={handleInputKeyDown}
                  onChange={(value) =>
                    handleCostComponentChange('labour', value)
                  }
                />
              </Form.Item>
            </Col>

            <Col span={isDesktopViewport ? 12 : 24}>
              <Form.Item
                name="materialCost"
                label="Materials"
                rules={createCostValidation()}
              >
                <InputNumber
                  id="instructions-analysis-material-cost-input"
                  className="width-percent-100"
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  disabled={isManualTotal}
                  onKeyDown={handleInputKeyDown}
                  onChange={(value) =>
                    handleCostComponentChange('materials', value)
                  }
                />
              </Form.Item>
            </Col>

            <Col span={isDesktopViewport ? 12 : 24}>
              <Form.Item
                name="machineryCost"
                label="Machinery"
                rules={createCostValidation()}
              >
                <InputNumber
                  id="instructions-analysis-machinery-cost-input"
                  className="width-percent-100"
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  disabled={isManualTotal}
                  onKeyDown={handleInputKeyDown}
                  onChange={(value) =>
                    handleCostComponentChange('machinery', value)
                  }
                />
              </Form.Item>
            </Col>

            <Col span={isDesktopViewport ? 12 : 24}>
              <Form.Item
                name="otherCost"
                label="Others"
                rules={createCostValidation()}
              >
                <InputNumber
                  id="instructions-analysis-other-cost-input"
                  className="width-percent-100"
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  disabled={isManualTotal}
                  onKeyDown={handleInputKeyDown}
                  onChange={(value) =>
                    handleCostComponentChange('others', value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="totalCost"
                label={
                  <Space>
                    <span>Total Cost of Quality</span>
                    {isUsingComponents && (
                      <span className="text-secondary">(Auto-calculated)</span>
                    )}
                  </Space>
                }
                rules={createCostValidation(
                  isUsingComponents ? 3999999996 : 99999999999,
                )}
              >
                <InputNumber
                  id="instructions-analysis-total-cost-input"
                  className="width-percent-100"
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  disabled={isUsingComponents}
                  onKeyDown={handleInputKeyDown}
                  onChange={handleTotalChange}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
        {isErrorExist && (
          <div className="text-danger">
            Enter either the total cost or at least one component from Labour,
            Material, Machinery or Others
          </div>
        )}

        <div className="form-buttons mt-20">
          <Button
            id="instructions-analysis-cancel-btn"
            shape="round"
            className="cancel-button"
            onClick={handleCancel}
            disabled={submitLoading}
          >
            Cancel
          </Button>
          <Button
            id="instructions-analysis-save-btn"
            shape="round"
            type="primary"
            className="save-button"
            htmlType="submit"
            loading={submitLoading}
            onClick={() => {
              if (
                !(
                  costComponents?.labour +
                    costComponents?.materials +
                    costComponents?.machinery +
                    costComponents?.others +
                    costComponents?.total >
                  0
                )
              ) {
                setIsErrorExist(true);
              }
            }}
          >
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default InstructionAnalysisModal;
